const OUTREACH_CONTEXT_MAX = 600;

function escapeHtml(str) {
  return String(str ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function splitMarkdownTableRow(line) {
  return line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((cell) => cell.trim());
}

function isMarkdownTableSeparator(line) {
  return /^\s*\|?[\s\-:|]+\|?\s*$/.test(line);
}

function isMarkdownTableRow(line) {
  return /^\s*\|.*\|\s*$/.test(line);
}

/** Convert pipe rows to tab-separated; drop separator rows. */
function markdownTablesToTsv(text) {
  return String(text ?? '')
    .split('\n')
    .filter((line) => !/^\s*\|[\s:|-]+\|\s*$/.test(line))
    .map((line) => {
      if (!isMarkdownTableRow(line)) return line;
      return line
        .trim()
        .replace(/^\||\|$/g, '')
        .split('|')
        .map((cell) => cell.trim())
        .join('\t');
    })
    .join('\n');
}

function removeMarkdownTableLines(text) {
  return String(text ?? '')
    .split('\n')
    .filter((line) => {
      if (/^\s*\|[\s:|-]+\|\s*$/.test(line)) return false;
      if (isMarkdownTableRow(line)) return false;
      return true;
    })
    .join('\n');
}

/** Drop GFM pipe tables so structured ChatBlocks are not duplicated as markdown. */
export function stripChatMarkdownTables(text) {
  return removeMarkdownTableLines(text).replace(/\n{3,}/g, '\n\n');
}

function rowsToHtmlTable(rows) {
  if (!rows?.length) return '';
  const [headerRow, ...bodyRows] = rows;
  const header = headerRow.map((cell) => `<th>${escapeHtml(cell)}</th>`).join('');
  const body = bodyRows
    .map((row) => `<tr>${row.map((cell) => `<td>${escapeHtml(cell)}</td>`).join('')}</tr>`)
    .join('');
  return (
    '<table border="1" cellpadding="6" cellspacing="0" ' +
    'style="border-collapse:collapse;font-size:13px;margin:8px 0;">' +
    `<thead><tr>${header}</tr></thead>` +
    `<tbody>${body}</tbody></table>`
  );
}

function parseMarkdownTableSegment(lines, startIndex) {
  const rows = [];
  let i = startIndex;
  while (i < lines.length && lines[i].includes('|')) {
    if (!isMarkdownTableSeparator(lines[i])) {
      rows.push(splitMarkdownTableRow(lines[i]));
    }
    i += 1;
  }
  return { rows, nextIndex: i };
}

function markdownInlineToHtml(text) {
  const raw = String(text ?? '');
  if (!raw) return '';
  const hasMarkup = /(\*\*|\*|`|\[)/.test(raw);
  if (!hasMarkup) return escapeHtml(raw);
  return raw
    .replace(/\[([^\]]+)\]\(neogtm:\/\/[^)]+\)/g, (_, label) => escapeHtml(label))
    .replace(
      /\[([^\]]+)\]\(([^)]+)\)/g,
      (_, label, url) => `<a href="${escapeHtml(url)}">${escapeHtml(label)}</a>`,
    )
    .replace(/\*\*([^*]+)\*\*/g, (_, value) => `<strong>${escapeHtml(value)}</strong>`)
    .replace(/\*([^*]+)\*/g, (_, value) => `<em>${escapeHtml(value)}</em>`)
    .replace(/`([^`]+)`/g, (_, value) => `<code>${escapeHtml(value)}</code>`);
}

function markdownProseLinesToHtml(lines) {
  const chunks = [];
  let paragraph = [];

  const flushParagraph = () => {
    if (!paragraph.length) return;
    const joined = paragraph.join(' ').trim();
    if (joined) chunks.push(`<p>${markdownInlineToHtml(joined)}</p>`);
    paragraph = [];
  };

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) {
      flushParagraph();
      continue;
    }
    if (/^#{1,6}\s+/.test(line)) {
      flushParagraph();
      chunks.push(`<p><strong>${markdownInlineToHtml(line.replace(/^#{1,6}\s+/, ''))}</strong></p>`);
      continue;
    }
    if (/^[-*+]\s+/.test(line)) {
      flushParagraph();
      chunks.push(`<p>• ${markdownInlineToHtml(line.replace(/^[-*+]\s+/, ''))}</p>`);
      continue;
    }
    paragraph.push(line);
  }
  flushParagraph();
  return chunks.join('\n');
}

function markdownToHtml(md, { stripTables = false } = {}) {
  const lines = String(md ?? '').split('\n');
  const parts = [];
  let proseBuffer = [];
  let i = 0;

  const flushProse = () => {
    if (!proseBuffer.length) return;
    const html = markdownProseLinesToHtml(proseBuffer);
    if (html) parts.push(html);
    proseBuffer = [];
  };

  while (i < lines.length) {
    const line = lines[i];
    if (line.includes('|') && i + 1 < lines.length && isMarkdownTableSeparator(lines[i + 1])) {
      flushProse();
      const { rows, nextIndex } = parseMarkdownTableSegment(lines, i);
      if (stripTables) {
        i = nextIndex;
        continue;
      }
      const tableHtml = rowsToHtmlTable(rows);
      if (tableHtml) parts.push(tableHtml);
      i = nextIndex;
      continue;
    }
    proseBuffer.push(line);
    i += 1;
  }
  flushProse();
  return parts.join('\n');
}

export function chatAnswerToPlainText(md, { stripTables = false } = {}) {
  let text = String(md ?? '');
  text = stripTables ? removeMarkdownTableLines(text) : markdownTablesToTsv(text);

  return text
    .replace(/\[([^\]]+)\]\(neogtm:\/\/[^)]+\)/g, '$1')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1 ($2)')
    .replace(/^\s{0,3}#{1,6}\s+/gm, '')
    .replace(/^\s{0,3}>\s+/gm, '')
    .replace(/^\s{0,3}[-*+]\s+/gm, '• ')
    .replace(/^\s{0,3}\d+\.\s+/gm, '')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/(^|[^*])\*([^*]+)\*/g, '$1$2')
    .replace(/~~([^~]+)~~/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/^-{3,}$/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** Parse GFM pipe tables from markdown source. */
export function parseMarkdownTables(md) {
  const lines = String(md ?? '').split('\n');
  const tables = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    if (line.includes('|') && i + 1 < lines.length && isMarkdownTableSeparator(lines[i + 1])) {
      const { rows, nextIndex } = parseMarkdownTableSegment(lines, i);
      if (rows.length) tables.push(rows);
      i = nextIndex;
      continue;
    }
    i += 1;
  }

  return tables;
}

function isNullishBlockCell(value, kind) {
  if (kind === 'date') return value == null || value === '';
  if (kind === 'link') {
    return value == null || (typeof value === 'object' && !value.label && !value.url);
  }
  return value == null || value === '';
}

function blockCellToCopyValue(value, kind) {
  if (isNullishBlockCell(value, kind)) {
    return kind === 'date' ? 'date unknown' : '';
  }
  if (kind === 'link' && value && typeof value === 'object') {
    const label = String(value.label || '').trim();
    const url = String(value.url || '').trim();
    if (label && url && label !== url) return `${label} (${url})`;
    return label || url;
  }
  return String(value);
}

function blockCellToHtml(value, kind) {
  if (isNullishBlockCell(value, kind)) {
    return kind === 'date' ? escapeHtml('date unknown') : '&nbsp;';
  }
  if (kind === 'link' && value && typeof value === 'object' && value.url) {
    const label = escapeHtml(value.label || value.url);
    const url = escapeHtml(value.url);
    return `<a href="${url}">${label}</a>`;
  }
  return escapeHtml(blockCellToCopyValue(value, kind));
}

export function blockTableToTsv(block) {
  if (!block?.columns?.length) return '';
  const header = block.columns.map((col) => col.label).join('\t');
  const body = (block.rows || []).map((row) =>
    block.columns.map((col) => blockCellToCopyValue(row[col.key], col.kind)).join('\t'),
  );
  return [header, ...body].join('\n');
}

function blockTableToHtml(block) {
  if (!block?.columns?.length) return '';
  const header = block.columns.map((col) => `<th>${escapeHtml(col.label)}</th>`).join('');
  const body = (block.rows || [])
    .map((row) => {
      const cells = block.columns
        .map((col) => `<td>${blockCellToHtml(row[col.key], col.kind)}</td>`)
        .join('');
      return `<tr>${cells}</tr>`;
    })
    .join('');
  return (
    '<table border="1" cellpadding="6" cellspacing="0" ' +
    'style="border-collapse:collapse;font-size:13px;margin:8px 0;">' +
    `<thead><tr>${header}</tr></thead>` +
    `<tbody>${body}</tbody></table>`
  );
}

export function blockChartToTsv(block) {
  const slices = Array.isArray(block?.slices) ? block.slices : [];
  if (!slices.length) return '';
  const unit = block.unit || 'value';
  const header = `Slice\t${unit}`;
  const body = slices.map((slice) => `${slice.label}\t${slice.value}`);
  return [header, ...body].join('\n');
}

function blockChartToHtml(block) {
  const slices = Array.isArray(block?.slices) ? block.slices : [];
  if (!slices.length) return '';
  const unit = escapeHtml(block.unit || 'value');
  const rows = slices
    .map(
      (slice) =>
        `<tr><td>${escapeHtml(slice.label)}</td><td>${escapeHtml(String(slice.value))}</td></tr>`,
    )
    .join('');
  return (
    '<table border="1" cellpadding="6" cellspacing="0" ' +
    'style="border-collapse:collapse;font-size:13px;margin:8px 0;">' +
    `<thead><tr><th>Slice</th><th>${unit}</th></tr></thead>` +
    `<tbody>${rows}</tbody></table>`
  );
}

function appendStructuredBlockCopy(parts, block) {
  if (block?.type === 'table') {
    const tsv = blockTableToTsv(block);
    if (!tsv) return;
    if (block.title) parts.push(block.title);
    parts.push(tsv);
    if (block.note) parts.push(block.note);
    return;
  }
  if (block?.type === 'chart') {
    const tsv = blockChartToTsv(block);
    if (!tsv) return;
    if (block.title) parts.push(block.title);
    parts.push(tsv);
    if (block.note) parts.push(block.note);
  }
}

function appendStructuredBlockHtml(parts, block) {
  if (block?.type === 'table') {
    if (block.title) {
      parts.push(`<p><strong>${escapeHtml(block.title)}</strong></p>`);
    }
    const tableHtml = blockTableToHtml(block);
    if (tableHtml) parts.push(tableHtml);
    if (block.note) {
      parts.push(`<p style="font-size:11px;color:#5C6B7A;">${escapeHtml(block.note)}</p>`);
    }
    return;
  }
  if (block?.type === 'chart') {
    if (block.title) {
      parts.push(`<p><strong>${escapeHtml(block.title)}</strong></p>`);
    }
    const chartHtml = blockChartToHtml(block);
    if (chartHtml) parts.push(chartHtml);
    if (block.note) {
      parts.push(`<p style="font-size:11px;color:#5C6B7A;">${escapeHtml(block.note)}</p>`);
    }
  }
}

export function chatMessageToCopyText(message) {
  const parts = [];
  const blocks = Array.isArray(message?.blocks) ? message.blocks : [];
  const structuredTables = blocks.filter((block) => block?.type === 'table');

  for (const block of blocks) {
    appendStructuredBlockCopy(parts, block);
  }

  const plain = chatAnswerToPlainText(message?.text, {
    stripTables: structuredTables.length > 0,
  });
  if (plain) parts.push(plain);

  return parts.join('\n\n').trim();
}

export function chatMessageToCopyHtml(message) {
  const parts = [];
  const blocks = Array.isArray(message?.blocks) ? message.blocks : [];
  const structuredTables = blocks.filter((block) => block?.type === 'table');

  for (const block of blocks) {
    appendStructuredBlockHtml(parts, block);
  }

  const proseHtml = markdownToHtml(message?.text, {
    stripTables: structuredTables.length > 0,
  });
  if (proseHtml) parts.push(proseHtml);

  return parts.join('\n');
}

function wrapHtmlForClipboard(htmlBody) {
  return `<!DOCTYPE html><html><body><!--StartFragment-->${htmlBody}<!--EndFragment--></body></html>`;
}

function copyViaExecCommandPlain(value) {
  if (typeof document === 'undefined') return false;
  try {
    const textarea = document.createElement('textarea');
    textarea.value = value;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.top = '0';
    textarea.style.left = '0';
    textarea.style.opacity = '0';
    textarea.style.pointerEvents = 'none';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    textarea.setSelectionRange(0, value.length);
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
}

/** Set text/html and text/plain via a synchronous copy event (works in click handlers). */
function copyViaCopyEvent(html, plainText) {
  if (typeof document === 'undefined') return false;
  try {
    const onCopy = (event) => {
      event.clipboardData.setData('text/html', html);
      event.clipboardData.setData('text/plain', plainText);
      event.preventDefault();
    };
    document.addEventListener('copy', onCopy);
    const ok = document.execCommand('copy');
    document.removeEventListener('copy', onCopy);
    return ok;
  } catch {
    return false;
  }
}

/** Copy plain text only — runs execCommand synchronously first to preserve the user gesture. */
export function copyTextToClipboard(text) {
  const value = String(text ?? '');
  if (!value) return Promise.resolve(false);

  if (copyViaExecCommandPlain(value)) return Promise.resolve(true);

  if (navigator.clipboard?.writeText) {
    return navigator.clipboard.writeText(value).then(
      () => true,
      () => false,
    );
  }

  return Promise.resolve(false);
}

/**
 * Copy a chat message for paste into email or Sheets.
 * Writes text/html (real tables) plus text/plain (TSV fallback) when supported.
 */
export function copyChatMessageToClipboard(message) {
  const plain = chatMessageToCopyText(message);
  if (!plain) return Promise.resolve(false);

  const htmlBody = chatMessageToCopyHtml(message);
  const htmlForClipboard = wrapHtmlForClipboard(htmlBody);

  if (copyViaCopyEvent(htmlForClipboard, plain)) {
    return Promise.resolve(true);
  }

  if (navigator.clipboard?.write) {
    try {
      const htmlBlob = new Blob([htmlForClipboard], { type: 'text/html' });
      const plainBlob = new Blob([plain], { type: 'text/plain' });
      return navigator.clipboard
        .write([new ClipboardItem({ 'text/html': htmlBlob, 'text/plain': plainBlob })])
        .then(
          () => true,
          () => copyTextToClipboard(plain),
        );
    } catch {
      return copyTextToClipboard(plain);
    }
  }

  return copyTextToClipboard(plain);
}

export function buildOutreachContextFromAnswer(md) {
  const plain = chatAnswerToPlainText(md);
  const tables = parseMarkdownTables(md);

  if (!tables.length) {
    if (plain.length <= OUTREACH_CONTEXT_MAX) return plain;
    return `${plain.slice(0, OUTREACH_CONTEXT_MAX - 1)}…`;
  }

  const [headerRow, ...bodyRows] = tables[0];
  const summaryLine = plain.split('\n').find((line) => line.trim()) || 'Key signals from chat:';
  const topRows = bodyRows.slice(0, 3);
  const parts = [
    summaryLine,
    headerRow?.join(' | ') ?? '',
    ...topRows.map((row) => row.join(' | ')),
  ].filter(Boolean);

  let context = parts.join('\n');
  if (context.length > OUTREACH_CONTEXT_MAX) {
    context = `${context.slice(0, OUTREACH_CONTEXT_MAX - 1)}…`;
  }
  return context.trim();
}
